import { DotsVertical } from '@timelyapp/icons/solid-icons';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import React, { forwardRef, useCallback, useContext, useEffect } from 'react';
import { useEffectOnValueChange } from '../../hooks/useEffectOnValueChange';
import { useInterval } from '../../hooks/useInterval';
import { usePrevious } from '../../hooks/usePrevious';
import { Box } from '../box/Box';
import type { ElementProps } from '../element/Element';
import { Element } from '../element/Element';
import { Text } from '../text/Text';
import { TimelineAdjustableSelection } from './components/Timeline.AdjustableSelection';
import {
  TimelineContext,
  TimelineContextContainer,
  TimelineContextControls,
} from './components/Timeline.Context';
import { TimelineEvent } from './components/Timeline.Event';
import { TimelineMemory } from './components/Timeline.Memory';
import { TimelineSelection } from './components/Timeline.Selection';
import { TimelineSuggestion } from './components/Timeline.Suggestion';
import TimelineSummaryBlock from './components/Timeline.SummaryBlock';
import styles from './Timeline.module.scss';
import type { TimelineConfigProps, TimelineItemPositioningProps } from './Timeline.types';
import { timelineItemPositioning } from './Timeline.utils';

export interface Props extends ElementProps {}

const TimelineContainer = forwardRef<
  HTMLDivElement,
  Props &
    TimelineConfigProps & {
      showFloatingControls?: boolean;
    }
>((props, ref) => {
  const {
    orientation,
    ticksPerInterval,
    onZoomToPx,
    fromDateTime,
    pxPerMinute,
    maxPxPerMinute,
    minPxPerMinute,
    // local
    showFloatingControls,
    style,
    className,
    ...rest
  } = props;

  return (
    <TimelineContextContainer
      ref={ref}
      style={style}
      className={className}
      config={{
        orientation,
        ticksPerInterval,
        pxPerMinute,
        fromDateTime,
        onZoomToPx,
        maxPxPerMinute,
        minPxPerMinute,
      }}
    >
      <TimelineInnerScrollarea {...rest} />
      {/** Quick & dirty, only used from Timely atm. This should make "absolute" positioning much easier */}
      {!!showFloatingControls && <Timeline.FloatingControls />}
    </TimelineContextContainer>
  );
});
TimelineContainer.displayName = 'Timeline';

// Intentionally not a forwardRef to avoid confusion with the various refs below.
const TimelineInnerScrollarea = (
  props: Props & { scrollStartDateTime?: DateTime | number | null }
) => {
  const [scrollInitialized, setScrollInitialized] = React.useState(false);
  const {
    orientation,
    isZoomMode,
    isDragMode,
    isDragSubMode,
    isPanMode,
    panRef,
    fromDateTime,
    pxPerMinute,
    scrollAreaRef,
  } = useContext(TimelineContext);
  const { scrollStartDateTime = fromDateTime.plus({ hours: 6.5 }), ...rest } = props;

  const handleScrollToPx = useCallback(
    (distancePx: number, behavior?: ScrollBehavior) => {
      scrollAreaRef?.scroll({
        top: orientation === 'vertical' ? distancePx : undefined,
        left: orientation === 'horizontal' ? distancePx : undefined,
        behavior,
      });
    },
    [scrollAreaRef, orientation]
  );

  /**
   * Scroll to `scrollStartDateTime` on initial mount and when value changes.
   */
  const prevScrollTo = usePrevious(scrollStartDateTime);
  useEffect(() => {
    let scrollToDateTime: DateTime | undefined;
    if (typeof scrollStartDateTime === 'number') {
      // when scrollStartDateTime is a number, it describes where the current time should be in the scroll area
      scrollToDateTime = DateTime.now().minus({
        minutes: (scrollStartDateTime * (scrollAreaRef?.clientHeight ?? 0)) / pxPerMinute,
      });
    } else if (scrollStartDateTime) {
      scrollToDateTime = scrollStartDateTime;
    } else {
      // Don't mark as initialized
      return;
    }

    const noChanges =
      !scrollAreaRef ||
      !scrollToDateTime ||
      (typeof prevScrollTo === 'number'
        ? prevScrollTo === scrollStartDateTime
        : typeof scrollStartDateTime === 'object' &&
          prevScrollTo?.toUnixInteger() === scrollStartDateTime?.toUnixInteger());

    // Abort if no changes, unless it's the first time rendering
    if (noChanges && scrollInitialized) return;
    if (!scrollInitialized) setScrollInitialized(true);
    if (!scrollToDateTime) return;

    const distancePx =
      scrollToDateTime.diff(fromDateTime, 'minutes').minutes * pxPerMinute -
      // Account for column headers
      40;
    handleScrollToPx(distancePx, scrollInitialized ? 'smooth' : undefined);
  }, [
    scrollStartDateTime,
    scrollAreaRef,
    prevScrollTo,
    scrollInitialized,
    fromDateTime,
    pxPerMinute,
    handleScrollToPx,
  ]);

  /**
   * Keep ~same scroll position when zooming in/out (via pxPerMinute changing).
   */
  const previousPixelsPerMinute = usePrevious(pxPerMinute);
  useEffectOnValueChange(pxPerMinute, () => {
    if (!scrollAreaRef || !scrollInitialized) return;

    // This calculates the center of the visible view relative to the total scrollable container
    // height, which we can subtract or add to the new scroll position to zoom around the "middle"
    // NB: horizontal is untested, might work, might not.
    const focalPoint =
      orientation === 'horizontal'
        ? scrollAreaRef.clientWidth ** 2 / scrollAreaRef.scrollWidth / 2
        : scrollAreaRef.clientHeight ** 2 / scrollAreaRef.scrollHeight / 2;

    const scale = pxPerMinute / previousPixelsPerMinute;
    const zoomDirection = scale > 1 ? 1 : -1;
    const distancePx = (scrollAreaRef.scrollTop + focalPoint * zoomDirection) * scale;

    handleScrollToPx(distancePx);
  });

  return (
    <Element
      ref={panRef}
      data-timeline-orientation={orientation}
      // When "actively using a timeline tool", we want the Memory popouts++ to be "off"
      // Otherwise, there's a lot of Memories popping in/out of view when panning/zooming
      data-timeline-limit-interactions={isPanMode || isDragMode || isZoomMode}
      data-timeline-subtractive-interactions={isDragSubMode}
      {...rest}
      className={classNames(styles.timelineScrollarea, props.className)}
    />
  );
};
TimelineInnerScrollarea.displayName = 'Timeline.InnerScrollarea';

type TimelineLaneVariants = 'subtle' | 'highlight' | 'success';

const TimelineLane = forwardRef<
  HTMLDivElement,
  Props & {
    /** Minimum size of this column */
    minSizePx?: number;
    /** Max size of this column, useful for collapsed control */
    maxSizePx?: number;
    variant?: TimelineLaneVariants;
    separator?: 'start' | 'end';
  }
>((props, ref) => {
  const { minSizePx, maxSizePx, variant, style, separator, ...rest } = props;
  const { orientation, ticksPerInterval, pxPerMinute } = useContext(TimelineContext);

  return (
    <Element
      ref={ref}
      style={{
        minWidth: orientation === 'vertical' ? minSizePx : undefined,
        minHeight: orientation === 'horizontal' ? minSizePx : undefined,
        maxWidth: orientation === 'vertical' ? maxSizePx : undefined,
        maxHeight: orientation === 'horizontal' ? maxSizePx : undefined,
        backgroundSize:
          orientation === 'vertical'
            ? `100% ${(pxPerMinute * 60) / ticksPerInterval}px`
            : ` ${(pxPerMinute * 60) / ticksPerInterval}px 100%`,
        ...style,
      }}
      {...rest}
      data-separator={separator}
      className={classNames(
        styles.lane,
        styles[`lane-variant-${variant}`],
        styles[`separator-${separator}`],
        props.className
      )}
    />
  );
});
TimelineLane.displayName = 'Timeline.Lane';

const TimelineLaneSublanes = forwardRef<HTMLDivElement, Props>((props, ref) => {
  return (
    <Element
      ref={ref}
      {...props}
      className={classNames(styles.laneSublanesContainer, props.className)}
    />
  );
});
TimelineLaneSublanes.displayName = 'Timeline.Lane.Sublanes';

type TimelineLaneHeaderProps = Props & {
  variant?: TimelineLaneVariants;
  /** When interactive, hover effects are enabled */
  interactive?: boolean;
  /** Permanent hover state, @requires `interactive: true` */
  active?: boolean;
};
const TimelineLaneHeader = forwardRef<HTMLDivElement, TimelineLaneHeaderProps>((props, ref) => {
  const { variant, interactive, active, children, ...rest } = props;
  return (
    <Element
      ref={ref}
      {...rest}
      className={classNames(styles.laneHeader, styles[`lane-variant-${variant}`])}
      data-no-drag-select // Prevent clicks on headers from starting a drag selection
    >
      <Box.Row
        gap={props.gap}
        className={classNames(
          styles.laneHeaderContent,
          {
            [styles.active]: active && interactive,
            [styles.interactive]: interactive,
          },
          props.className
        )}
      >
        {children}
      </Box.Row>
    </Element>
  );
});
TimelineLaneHeader.displayName = 'Timeline.Lane.Header';

const TimelineLaneHeaderDropdownIndicator = ({ isOpen }: { isOpen: boolean }) => {
  return (
    <DotsVertical
      style={{
        color: isOpen ? 'var(--theme_color_content_normal)' : 'var(--theme_color_content_subtle)',
        flexShrink: 0,
        flexBasis: 14,
      }}
    />
  );
};
TimelineLaneHeaderDropdownIndicator.displayName = 'Timeline.Lane.Header.DropdownIndicator';

const TimelineLaneGutterMark = forwardRef<HTMLDivElement, Props & TimelineItemPositioningProps>(
  (props, ref) => {
    const { style, sizePx, offsetPx, ...rest } = props;
    const dynamicStyles = timelineItemPositioning(props);
    return (
      <Element
        ref={ref}
        {...rest}
        style={{ ...dynamicStyles, ...style }}
        className={classNames(styles.laneGutterMark, props.className)}
      />
    );
  }
);
TimelineLaneGutterMark.displayName = 'Timeline.Lane.GutterMark';

const TimelineGutter = forwardRef<
  HTMLDivElement,
  Props & {
    variant?: TimelineLaneVariants;
  }
>(({ variant, ...props }, ref) => {
  return (
    <Element
      ref={ref}
      {...props}
      className={classNames(props.className, styles.gutterLane, styles[`lane-variant-${variant}`])}
    />
  );
});
TimelineGutter.displayName = 'Timeline.Gutter';

const TimelineStickyLanes = forwardRef<HTMLDivElement, Props>((props, ref) => {
  return (
    <Element ref={ref} {...props} className={classNames(props.className, styles.stickyLanes)} />
  );
});
TimelineStickyLanes.displayName = 'Timeline.StickyLanes';

const TimelineGutterInterval = forwardRef<
  HTMLDivElement,
  Props &
    Pick<TimelineItemPositioningProps, 'sizePx'> & {
      extraEmphasis?: boolean;
    }
>((props, ref) => {
  const { extraEmphasis, sizePx, ...rest } = props;
  const { orientation } = useContext(TimelineContext);

  const style = orientation === 'vertical' ? { height: sizePx } : { width: sizePx };
  return (
    <Element
      ref={ref}
      {...rest}
      style={{ ...rest.style, ...style }}
      className={classNames(
        styles.interval,
        { [styles.extraEmphasis]: extraEmphasis },
        props.className
      )}
    >
      <Text.Label
        size="xxsmall"
        color={extraEmphasis ? 'lowContrast' : 'minContrast'}
        className={styles.lifted}
      >
        {props.children}
      </Text.Label>
    </Element>
  );
});
TimelineGutterInterval.displayName = 'Timeline.Gutter.Interval';

const TimelineIndicator = forwardRef<HTMLDivElement, Props & { offsetPx: number }>((props, ref) => {
  const { offsetPx, style, ...rest } = props;
  const { orientation } = useContext(TimelineContext);

  // Re-render every minute to update the indicator
  useInterval(60 * 1000);

  return (
    <Element
      ref={ref}
      {...rest}
      style={{
        top: orientation === 'vertical' ? offsetPx : undefined,
        left: orientation === 'horizontal' ? offsetPx : undefined,
        ...style,
      }}
      className={classNames(props.className, styles.indicator)}
    />
  );
});
TimelineIndicator.displayName = 'Timeline.Indicator';

const TimelineBackgroundIndicator = forwardRef<HTMLDivElement, Props & { offsetPx: number }>(
  (props, ref) => {
    const { offsetPx, style, ...rest } = props;
    const { orientation } = useContext(TimelineContext);

    return (
      <Element
        ref={ref}
        {...rest}
        style={{
          top: orientation === 'vertical' ? offsetPx : undefined,
          left: orientation === 'horizontal' ? offsetPx : undefined,
          ...style,
        }}
        className={classNames(props.className, styles.backgroundIndicator)}
      />
    );
  }
);
TimelineBackgroundIndicator.displayName = 'Timeline.BackgroundIndicator';
/**
 * Context Container for Timeline
 * @example <Timeline orientation="vertical" pxPerMinute={3} ticksPerInterval={4}>...</Timeline>
 *
 * @notes
 * - Helps pass around props to subcomponents, like `orientation`, `pxPerMinute`, `ticksPerInterval`, etc
 * - Provides a `panRef` to the scrollable area
 */
export const Timeline = Object.assign(TimelineContainer, {
  /** Timeline Gutter (e.g timestamp container) */
  Gutter: Object.assign(TimelineGutter, {
    /** Timeline Gutter Interval (e.g, time ticks)
     * @example <Timeline.Gutter.Interval sizePx={100} />
     * @note Reads from `Timeline.Context`
     */
    Interval: TimelineGutterInterval,
    /**
     * Gutter Header (e.g, gutter title)
     * @example <Timeline.Gutter.Header>GMT+1</Timeline.Gutter.Header>
     * @note Reads from `Timeline.Context`
     */
    Header: TimelineLaneHeader,
  }),
  /**
   * Typically current time indicator
   * @example <Timeline.Indicator offsetPx={100} />
   * @note Reads from `Timeline.Context`
   */
  Indicator: TimelineIndicator,
  BackgroundIndicator: TimelineBackgroundIndicator,
  /** Stick one or more lanes to the left side of scrollable area */
  StickyLanes: TimelineStickyLanes,
  /**
   * Timeline Lane (e.g, memory lane)
   * @example <Timeline.Lane>
   *    <Timeline.Lane.Header>My Lane</Timeline.Lane.Header>
   *    <Timeline.Lane.Event sizePx={100} offsetPx={100}>
   *        Behold, an event!
   *    </Timeline.Lane.Event>
   * </Timeline.Lane>
   * @note Reads from `Timeline.Context`
   */
  Lane: Object.assign(TimelineLane, {
    /**
     * Lane Header (e.g, lane title)
     * @example <Timeline.Lane.Header>My Lane</Timeline.Lane.Header>
     */
    Header: Object.assign(TimelineLaneHeader, {
      DropdownIndicator: TimelineLaneHeaderDropdownIndicator,
    }),
    /**
     * For `Timeline.Lane`'s that contain multiple sublanes, wrap them in this container
     */
    Sublanes: TimelineLaneSublanes,
    /** Place a `Memory` in a `Timeline.Lane`
     * @example <Timeline.Lane.Memory sizePx={100} offsetPx={100} />
     */
    Memory: TimelineMemory,
    /** Place an `Event` in a `Timeline.Lane`
     * @example <Timeline.Lane.Event sizePx={100} offsetPx={100} />
     */
    Event: TimelineEvent,
    /** Place a `Suggestion` (aka Interval Prediction) in a `Timeline.Lane`
     * @example <Timeline.Lane.Suggestion sizePx={100} offsetPx={100} />
     */
    Suggestion: TimelineSuggestion,
    /**
     * Place a `SummaryBlock` in a `Timeline.Lane`
     * @example <Timeline.Lane.SummaryBlock title="Summary Block" offsetPx={100} sizePx={100} />
     */
    SummaryBlock: TimelineSummaryBlock,
    /**
     * @todo Implement based on generic `Timeline` needs, see `ClickDragAdjust` for inspiration
     * @planned Not yet implemented past basic UI
     */
    AdjustableSelection: TimelineAdjustableSelection,
    /** Place a small mark/indicator in the left gutter of a lane, e.g to indicate logged time to a project */
    GutterMark: TimelineLaneGutterMark,
  }),
  /**
   * Pan/Zoom controls floating on `Elevation` bottom/right of `Timeline`
   * @example <Timeline.FloatingControls />
   */
  FloatingControls: TimelineContextControls,
  /** Mark selection of time by click-dragging */
  Selection: TimelineSelection,
});

Timeline.displayName = 'Timeline';
Timeline.Lane.displayName = 'Timeline.Lane';
Timeline.Lane.Header.displayName = 'Timeline.Lane.Header';
Timeline.Lane.Sublanes.displayName = 'Timeline.Lane.Sublanes';
Timeline.Lane.Memory.displayName = 'Timeline.Lane.Memory';
Timeline.Lane.Event.displayName = 'Timeline.Lane.Event';
Timeline.Lane.Suggestion.displayName = 'Timeline.Lane.Suggestion';
Timeline.Lane.AdjustableSelection.displayName = 'Timeline.Lane.AdjustableSelection';
Timeline.Selection.displayName = 'Timeline.Selection';
Timeline.Lane.GutterMark.displayName = 'Timeline.Lane.GutterMark';
Timeline.Gutter.Header.displayName = 'Timeline.Gutter.Header';
Timeline.Gutter.Interval.displayName = 'Timeline.Gutter.Interval';
Timeline.Lane.SummaryBlock.displayName = 'Timeline.Lane.SummaryBlock';
Timeline.Gutter.displayName = 'Timeline.Gutter';
Timeline.Indicator.displayName = 'Timeline.Indicator';
Timeline.BackgroundIndicator.displayName = 'Timeline.BackgroundIndicator';
Timeline.StickyLanes.displayName = 'Timeline.StickyLanes';
Timeline.FloatingControls.displayName = 'Timeline.FloatingControls';
